// src/pages/client-tabs/ClientWorkTab.jsx
import React, { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { listWork } from '../../services/workService';

/* ----------------------------- helpers ----------------------------- */

const cx = (...xs) => xs.filter(Boolean).join(' ');

// Safe date (date-only)
const fmtDateOnly = (v) =>
  v
    ? new Date(v).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const prettyStatus = (s) =>
  !s
    ? '—'
    : String(s)
        .split('_')
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
        .join(' ');

// Dot color by type (align with calendar colors you’ve been using)
const typeDot = {
  task: 'bg-sky-500',
  job: 'bg-amber-500',
  appointment: 'bg-violet-500',
};

// Status chips (same mapping you use in WorkList)
const WORK_STATUS_CHIP = {
  todo: 'bg-slate-100 text-slate-700 border border-slate-200',
  new: 'bg-slate-100 text-slate-700 border border-slate-200',
  planned: 'bg-slate-100 text-slate-700 border border-slate-200',

  scheduled: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  confirmed: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  en_route: 'bg-sky-100 text-sky-700 border border-sky-200',
  in_progress: 'bg-blue-100 text-blue-700 border border-blue-200',
  blocked: 'bg-rose-100 text-rose-700 border border-rose-200',

  done: 'bg-green-100 text-green-700 border border-green-200',
  completed: 'bg-green-100 text-green-700 border border-green-200',
  invoiced: 'bg-green-100 text-green-700 border border-green-200',

  cancelled: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
  no_show: 'bg-orange-100 text-orange-800 border border-orange-200',
};

// Minimal filters
const TYPE_OPTIONS = [
  { label: 'Task', value: 'task' },
  { label: 'Job', value: 'job' },
  { label: 'Appointment', value: 'appointment' },
];

// Full union of statuses used across task/job/appointment
const STATUS_OPTIONS = [
  'todo',
  'in_progress',
  'blocked',
  'done',
  'cancelled',
  'new',
  'scheduled',
  'en_route',
  'completed',
  'invoiced',
  'planned',
  'confirmed',
  'no_show',
].map((v) => ({ label: prettyStatus(v), value: v }));

// Transition hints (same as WorkList)
const HINT_TRANSITIONS = {
  task: {
    todo: ['in_progress', 'blocked', 'done', 'cancelled'],
    in_progress: ['blocked', 'done', 'cancelled'],
    blocked: ['in_progress', 'done', 'cancelled'],
    done: ['in_progress', 'cancelled'],
    cancelled: ['todo'],
  },
  job: {
    new: ['scheduled', 'en_route', 'in_progress', 'cancelled'],
    scheduled: ['en_route', 'in_progress', 'cancelled'],
    en_route: ['in_progress', 'cancelled'],
    in_progress: ['completed', 'cancelled', 'no_show'],
    completed: ['invoiced', 'cancelled'],
    invoiced: [],
    cancelled: [],
    no_show: ['scheduled'],
  },
  appointment: {
    planned: ['confirmed', 'in_progress', 'cancelled', 'no_show'],
    confirmed: ['in_progress', 'cancelled', 'no_show'],
    in_progress: ['done', 'cancelled'],
    done: [],
    cancelled: [],
    no_show: ['planned'],
  },
};

function nextStatusesFor(type, status, item) {
  const base = HINT_TRANSITIONS[type]?.[status] ?? [];
  if (type === 'job') {
    return base.map((s) => ({
      value: s,
      disabled: s === 'invoiced' && !item?.invoiceId,
      reason: s === 'invoiced' && !item?.invoiceId ? 'Link an invoice first' : undefined,
    }));
  }
  return base.map((s) => ({ value: s, disabled: false }));
}

// Normalize assignees → [{id,name}]
function AssigneeGroup({ assignees = [] }) {
  const norm = (assignees || []).map((a, idx) => {
    if (a && typeof a === 'object') {
      const id = a.id || a._id || String(idx);
      const name = a.name || a.email || `User ${String(id).slice(-6)}`;
      return { id: String(id), name };
    }
    const id = String(a);
    return { id, name: `User ${id.slice(-6)}` };
  });
  if (!norm.length) return <span className="text-slate-400">—</span>;
  const shown = norm.slice(0, 3);
  const extra = norm.length - shown.length;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {shown.map((u) => (
        <span
          key={u.id}
          className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
          title={u.name}
        >
          {u.name}
        </span>
      ))}
      {extra > 0 && <span className="text-xs text-slate-500">+{extra} more</span>}
    </div>
  );
}

const SORTABLE = [
  { id: 'workNumber', label: 'Work #' },
  { id: 'title', label: 'Title' },
  { id: 'date', label: 'Date' },
  { id: 'status', label: 'Status' },
];

/* ----------------------------- portaled menu ----------------------------- */
function ActionMenuPortal({
  anchorEl,
  row,
  canEdit,
  canTransition,
  canDelete,
  onClose,
  onOpenDetails,
  onEdit,
  onTransition,
  onDelete,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const menuW = 256;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);
    let top = r.bottom + gap;
    let origin = 'top-right';

    setStyle({ top, left, origin });

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      const w = el.offsetWidth;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      if (left + w > vw - 8) {
        left = Math.max(8, vw - w - 8);
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = String(row?._id || row?.id || '');

  const type = String(row?.type || 'task').toLowerCase();
  const options = nextStatusesFor(type, row?.status, row);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 60 }}
      className={`min-w-[256px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin}`}
    >
      <div className="py-1 text-sm">
        <button
          role="menuitem"
          disabled={!canEdit}
          onClick={() => {
            onClose();
            if (onOpenDetails && id) onOpenDetails(id);
            else if (onEdit) onEdit(row);
          }}
          className={cx(
            'flex w-full items-center gap-2 px-3 py-2 text-left',
            canEdit ? 'hover:bg-slate-50 text-slate-700' : 'opacity-50 cursor-not-allowed text-slate-400'
          )}
        >
          <FiEdit2 className="text-slate-500" />
          Edit / View
        </button>

        <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400">
          Move to
        </div>

        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-slate-400">No actions</div>
        )}

        {options.map((opt) => (
          <button
            key={opt.value}
            role="menuitem"
            disabled={!canTransition || opt.disabled}
            title={opt.disabled ? opt.reason : undefined}
            onClick={() => {
              onClose();
              onTransition?.(row, opt.value);
            }}
            className={cx(
              'flex w-full items-center gap-2 px-3 py-2 text-left',
              !opt.disabled && canTransition
                ? 'hover:bg-slate-50 text-slate-700'
                : 'opacity-50 cursor-not-allowed text-slate-400'
            )}
          >
            <span className="inline-block h-2 w-2 rounded-full bg-slate-300" />
            <span className="capitalize">{opt.value.replace(/_/g, ' ')}</span>
          </button>
        ))}

        <div className="my-1 border-t border-slate-200" />

        <button
          role="menuitem"
          disabled={!canDelete}
          onClick={() => {
            onClose();
            onDelete?.(row);
          }}
          className={cx(
            'flex w-full items-center gap-2 px-3 py-2 text-left',
            canDelete ? 'hover:bg-rose-50 text-rose-600' : 'opacity-50 cursor-not-allowed text-slate-400'
          )}
        >
          <FiTrash2 />
          Delete
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ----------------------------- main: client-scoped tab ----------------------------- */

export default function ClientWorkTab({
  clientId,                // <-- REQUIRED for scoping
  works,                   // optional pre-fetched list (if provided, we'll use it)
  canEdit = false,         // admin|staff gate
  canTransition = true,
  canDelete = true,
  onOpenDetails,           // (workId) => open drawer
  onEditWork,              // (row) => fallback editor
  onTransitionWork,        // (row, nextStatus) => mutate
  onDeleteWork,            // (row) => delete/archive
  onRefreshSummary,        // optional: refresh parent badges/KPIs after mutations
}) {
  const navigate = useNavigate();

  // 🔓 open details with 3-step fallback (prop → global event → route nav)
  const openDetails = useCallback(
    (wid) => {
      const id = String(wid || '');
      if (!id) return;
      console.debug('[ClientWorkTab] openDetails', { id, hasOnOpenDetails: !!onOpenDetails });

      if (onOpenDetails) {
        onOpenDetails(id);
        return;
      }

      // global drawer listener fallback
      try {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('asteriq:open-work-drawer', { detail: { workId: id } })
          );
        }
      } catch {}

      // route fallback so something visible always happens
      if (navigate) {
        navigate(`/work/${encodeURIComponent(id)}`);
      }
    },
    [onOpenDetails, navigate]
  );

  // Fetch state (client-scoped)
  const [serverItems, setServerItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Minimal filter bar
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');     // task|job|appointment
  const [status, setStatus] = useState('all'); // union statuses
  const [sortBy, setSortBy] = useState('date'); // workNumber|title|date|status
  const [sortDir, setSortDir] = useState('desc');

  // Pagination (fixed 10/page like invoices tab)
  const [page, setPage] = useState(1);
  const limit = 10;

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Sticky clientId fetch
  const fetchWorks = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const { items } = await listWork({ clientId, limit: 200 });
      setServerItems(Array.isArray(items) ? items : []);
    } catch (err) {
      toast.error(err?.message || 'Failed to fetch work');
      setServerItems([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchWorks();
  }, [fetchWorks]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [search, type, status, sortBy, sortDir]);

  const resetFilters = () => {
    setSearch('');
    setType('all');
    setStatus('all');
    setSortBy('date');
    setSortDir('desc');
    setPage(1);
  };

  const getDate = useCallback((it) => it?.schedule?.start || it?.schedule?.end || it?.dueDate || null, []);

  // Source rows: prefer explicit prop if provided; else fetched list
  const sourceRows = useMemo(() => {
    if (Array.isArray(works) && works.length) return works;
    return serverItems;
  }, [works, serverItems]);

  /* ----------------------------- filtering & sorting ----------------------------- */
  const filtered = useMemo(() => {
    let rows = Array.isArray(sourceRows) ? sourceRows.slice() : [];

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const num = String(r.displayWorkNumber || r.workNumber || r.code || '').toLowerCase();
        const title = String(r.title || '').toLowerCase();
        const note = String(r.description || '').toLowerCase();
        return num.includes(q) || title.includes(q) || note.includes(q);
      });
    }

    if (type !== 'all') {
      rows = rows.filter((r) => String(r.type || '').toLowerCase() === type);
    }

    if (status !== 'all') {
      rows = rows.filter((r) => String(r.status || '').toLowerCase() === status);
    }

    rows.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const av =
        sortBy === 'date'
          ? getDate(a)
          : sortBy === 'workNumber'
          ? a?.displayWorkNumber || a?.workNumber || a?.code
          : sortBy === 'title'
          ? a?.title
          : sortBy === 'status'
          ? a?.status
          : null;

      const bv =
        sortBy === 'date'
          ? getDate(b)
          : sortBy === 'workNumber'
          ? b?.displayWorkNumber || b?.workNumber || b?.code
          : sortBy === 'title'
          ? b?.title
          : sortBy === 'status'
          ? b?.status
          : null;

      // dates
      if (sortBy === 'date') {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return dir * (at - bt);
      }

      // numeric aware string compare
      return dir * String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
    });

    return rows;
  }, [sourceRows, search, type, status, sortBy, sortDir, getDate]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / limit)),
    [filtered.length]
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page]);

  // After row mutations (transition/delete), refresh server list + parent KPIs if provided
  const refreshAfterMutation = useCallback(async () => {
    await fetchWorks();
    onRefreshSummary?.();
  }, [fetchWorks, onRefreshSummary]);

  /* ----------------------------- helpers ----------------------------- */
  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  /* ----------------------------- render ----------------------------- */
  return (
    <div className="w-full">
      {/* Minimal Filters Bar */}
      <div className="mb-4">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-3 overflow-x-auto">
          <div className="flex gap-2 items-center min-w-max">
            {/* Search */}
            <div className="relative w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search work #, title, notes…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Type */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setPage(1);
                }}
                title="Filter by type"
              >
                <option value="all">All Types</option>
                {TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Status */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                title="Filter by status"
              >
                <option value="all">All Statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort by */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                title="Sort by"
              >
                {SORTABLE.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort dir */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setPage(1);
                }}
                title="Sort direction"
              >
                <option value="desc">Newest</option>
                <option value="asc">Oldest</option>
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Reset */}
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
              onClick={resetFilters}
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="workNumber" label="Work #" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="title" label="Title" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-left font-bold">Assignees</th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="date" label="Date" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14">
                    <div className="text-center text-slate-600">Loading…</div>
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14">
                    <div className="text-center text-slate-600">No work found</div>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => {
                  const id = String(row?._id || row?.id || '');
                  const t = String(row?.type || 'task').toLowerCase();
                  const number = row.displayWorkNumber || row.workNumber || row.code || '—';
                  const title = row.title || '(Untitled)';
                  const date = getDate(row);
                  const assignees =
                    (Array.isArray(row?.assigneeSnapshots) && row.assigneeSnapshots.length
                      ? row.assigneeSnapshots
                      : Array.isArray(row?.assignees)
                      ? row.assignees
                      : []);

                  return (
                    <tr key={id} className="group transition hover:bg-blue-50/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => openDetails(id)}
                          title="Open details"
                        >
                          {number}
                        </button>
                        <div className="text-xs text-slate-500 capitalize">{t}</div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={cx('h-2.5 w-2.5 rounded-full', typeDot[t] || 'bg-slate-300')} />
                          {/* ▶ Title now opens the drawer (or route) */}
                          <button
                            type="button"
                            className="truncate text-left font-medium text-slate-900 hover:underline"
                            onClick={() => openDetails(id)}
                            title="Open details"
                          >
                            {title}
                          </button>
                        </div>
                        {!!row.description && (
                          <div className="max-w-[36rem] truncate text-xs text-slate-500">{row.description}</div>
                        )}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cx(
                            'px-2 py-0.5 rounded-full text-xs font-semibold',
                            WORK_STATUS_CHIP[row.status] || 'bg-slate-100 text-slate-700 border border-slate-200'
                          )}
                        >
                          {prettyStatus(row.status)}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <AssigneeGroup assignees={assignees} />
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnly(date)}</td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={menuRow && (menuRow._id || menuRow.id) === (row._id || row.id) ? 'true' : 'false'}
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 text-center text-slate-500">Loading…</div>
          ) : pageRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No work.</div>
          ) : (
            pageRows.map((row) => {
              const id = String(row?._id || row?.id || '');
              const t = String(row?.type || 'task').toLowerCase();
              const number = row.displayWorkNumber || row.workNumber || row.code || '—';
              const assignees =
                (Array.isArray(row?.assigneeSnapshots) && row.assigneeSnapshots.length
                  ? row.assigneeSnapshots
                  : Array.isArray(row?.assignees)
                  ? row.assignees
                  : []);
              const date = getDate(row);

              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        <button className="hover:underline" onClick={() => openDetails(id)} title="Open details">
                          {number}
                        </button>
                      </div>
                      <div className="text-xs text-slate-500 capitalize">{t}</div>

                      <div className="mt-1 flex items-center gap-2 text-sm text-slate-700">
                        <span className={cx('inline-block h-2.5 w-2.5 rounded-full', typeDot[t] || 'bg-slate-300')} />
                        <button
                          type="button"
                          className="truncate text-left hover:underline"
                          onClick={() => openDetails(id)}
                          title="Open details"
                        >
                          {row.title || '(Untitled)'}
                        </button>
                      </div>

                      <div className="mt-1 flex gap-2">
                        <span
                          className={cx(
                            'px-2 py-0.5 rounded-full text-xs font-semibold',
                            WORK_STATUS_CHIP[row.status] || 'bg-slate-100 text-slate-700 border border-slate-200'
                          )}
                        >
                          {prettyStatus(row.status)}
                        </span>
                      </div>

                      <div className="mt-1">
                        <AssigneeGroup assignees={assignees} />
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm text-slate-700">{fmtDateOnly(date)}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => openDetails(id)}
                    >
                      View
                    </button>
                    {canEdit && (
                      <>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={() => onEditWork?.(row)}
                        >
                          <FiEdit2 className="inline -mt-0.5 mr-1" />
                          Edit
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                          onClick={async () => {
                            await onDeleteWork?.(row);
                            await refreshAfterMutation();
                          }}
                        >
                          <FiTrash2 className="inline -mt-0.5 mr-1" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{page}</span> of{' '}
            <span className="font-semibold">{totalPages}</span> •{' '}
            <span className="font-semibold">{filtered.length}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canEdit={canEdit}
          canTransition={canTransition}
          canDelete={canDelete}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onOpenDetails={openDetails}
          onEdit={onEditWork}
          onTransition={async (row, next) => {
            await onTransitionWork?.(row, next);
            await refreshAfterMutation();
          }}
          onDelete={async (row) => {
            await onDeleteWork?.(row);
            await refreshAfterMutation();
          }}
        />
      )}
    </div>
  );
}
